import { useMemo } from 'react'
import Card from 'app/frontend/base/components/Card'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  eventParticipantStatuses,
  unavailableParticipantStatuses,
} from 'app/frontend/types/custom/EventParticipantStatus'
import { CharacterRole } from 'app/frontend/types/custom'
import { characterRoles } from 'app/frontend/types/custom/CharacterRole'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import capitalize from 'lodash/capitalize'
import { Badge } from '@/components/ui/badge'
import EventActivitySetupParticipant from './EventActivitySetupParticipant'

type Props = {
  activity: wowaudit.EventActivity
}

const roleSortOrder = new Map(
  characterRoles.map((role, index) => [role, index]),
)

const sortParticipantEntries = (
  entries: EventActivityParticipantEntry[],
  activityId: number,
) => {
  return entries.slice().sort((left, right) => {
    const leftSelection = left.selections[activityId]
    const rightSelection = right.selections[activityId]
    const leftRoleOrder =
      roleSortOrder.get(leftSelection.role as typeof characterRoles[number]) ??
      characterRoles.length
    const rightRoleOrder =
      roleSortOrder.get(rightSelection.role as typeof characterRoles[number]) ??
      characterRoles.length
    const roleOrderComparison = leftRoleOrder - rightRoleOrder
    if (roleOrderComparison !== 0) return roleOrderComparison

    return resolveEventParticipantCharacter(
      left,
      activityId,
    ).name.localeCompare(
      resolveEventParticipantCharacter(right, activityId).name,
    )
  })
}

const EventActivityBlock = ({ activity }: Props) => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const { allEntries } = useTeamEventParticipantsContext()
  const canAdminEdit = accessLevel === 'admin'
  const selectableStatuses = useMemo(
    () =>
      eventParticipantStatuses.filter(
        (status) =>
          canAdminEdit ||
          team.settings.raidsAvailableStatuses.value.includes(status),
      ),
    [canAdminEdit, team.settings.raidsAvailableStatuses.value],
  )
  const availableParticipants = useMemo(() => {
    return sortParticipantEntries(
      allEntries.filter(
        (entry) =>
          !unavailableParticipantStatuses.includes(entry.participant.status),
      ),
      activity.id,
    )
  }, [activity.id, allEntries])
  const unavailableParticipants = useMemo(() => {
    return sortParticipantEntries(
      allEntries.filter((entry) =>
        unavailableParticipantStatuses.includes(entry.participant.status),
      ),
      activity.id,
    )
  }, [activity.id, allEntries])

  const roleSections = useMemo(() => {
    const groupedByRole = new Map<
      string,
      {
        selectedEntries: EventActivityParticipantEntry[]
        benchedEntries: EventActivityParticipantEntry[]
      }
    >()

    availableParticipants.forEach((entry) => {
      const selection = entry.selections[activity.id]
      const existing = groupedByRole.get(selection.role) ?? {
        selectedEntries: [],
        benchedEntries: [],
      }
      if (selection.selected) {
        existing.selectedEntries.push(entry)
      } else {
        existing.benchedEntries.push(entry)
      }
      groupedByRole.set(selection.role, existing)
    })

    const orderedRoles = [
      ...characterRoles.filter((role) => groupedByRole.has(role)),
      ...Array.from(groupedByRole.keys())
        .filter((role) => !characterRoles.includes(role as CharacterRole))
        .sort((left, right) => left.localeCompare(right)),
    ]

    return orderedRoles.map((role) => ({
      role,
      selectedEntries: groupedByRole.get(role)?.selectedEntries ?? [],
      benchedEntries: groupedByRole.get(role)?.benchedEntries ?? [],
    }))
  }, [availableParticipants])

  return (
    <div className='space-y-4'>
      {roleSections.map(({ role, selectedEntries, benchedEntries }) => (
        <Card key={role} className='space-y-2'>
          <div className='flex items-center gap-1.5 text-sm text-foreground font-medium border-b pb-1'>
            <CharacterRoleIcon role={role as CharacterRole} />
            <span>{capitalize(role)}</span>
            <Badge variant='outline' className='ml-2 font-mono font-normal'>
              {selectedEntries.length}
            </Badge>
          </div>
          <div className='grid grid-cols-1 min-[500px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2'>
            {selectedEntries.map((entry) => {
              return (
                <EventActivitySetupParticipant
                  key={entry.participant.id}
                  entry={entry}
                  activity={activity}
                  selectableStatuses={selectableStatuses}
                  canAdminEdit={canAdminEdit}
                  variant='selected'
                />
              )
            })}

            {selectedEntries.length === 0 ? (
              <p className='text-sm text-muted-foreground italic'>Nobody</p>
            ) : null}
          </div>
          {benchedEntries.length ? (
            <div className='space-y-1'>
              <div className='text-[11px] text-muted-foreground font-medium'>
                Benched:
              </div>
              <div className='flex flex-wrap gap-1.5'>
                {benchedEntries.map((entry) => {
                  return (
                    <EventActivitySetupParticipant
                      key={entry.participant.id}
                      entry={entry}
                      activity={activity}
                      selectableStatuses={selectableStatuses}
                      canAdminEdit={canAdminEdit}
                      variant='benched'
                    />
                  )
                })}
              </div>
            </div>
          ) : null}
        </Card>
      ))}
      {unavailableParticipants.length ? (
        <div className='space-y-1'>
          <div className='text-[11px] text-muted-foreground font-medium'>
            Unavailable:
          </div>
          <div className='flex flex-wrap gap-1.5'>
            {unavailableParticipants.map((entry) => {
              return (
                <EventActivitySetupParticipant
                  key={entry.participant.id}
                  entry={entry}
                  activity={activity}
                  selectableStatuses={selectableStatuses}
                  canAdminEdit={canAdminEdit}
                  variant='unavailable'
                />
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default EventActivityBlock
